/* eslint-disable no-param-reassign */
import store from 'app/store';
import {
  CondenserType,
  ConfigurationType,
  LevelType,
  SidebarSectionType,
  SpaceType,
  ZoneItemType,
  ZoneType
} from 'data/types';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import {
  getAirHandlerPoolState,
  withAirHandlerPoolStaleness
} from 'utils/air-handler-availability';
import { getItem } from 'utils/data';
import { findSpaceById } from 'utils/structure';
import {
  currentPoolHashOf,
  validSystemsInputContextFromState
} from 'utils/valid-systems-input-context';

export function everyPositionedCondenserHasAirHandler(
  config: ConfigurationType | undefined
): boolean {
  if (!config) return false;
  const positioned = config.condensers.filter((c) => c.position !== undefined);
  if (positioned.length === 0) return false;
  return positioned.every((cond) =>
    cond.zones.some((z) => z.airHandler != null)
  );
}

export function everyZoneHasAirHandler(
  config: ConfigurationType | undefined
): boolean {
  if (!config) return false;
  const zones = config.condensers.flatMap((c) => c.zones);
  if (zones.length === 0) return false;
  return zones.every((z) => z.airHandler != null);
}

// Server copies carry no runtime fetch state. During the air-handler step the
// options pool (airHandlerOptions + ductingSummary) is the only runtime data
// a condenser has — it is re-attached whenever the previous copy held one,
// with its staleness re-evaluated against the hydrated inputs. The full rows
// still only survive when they had been fetched.
export function preserveRuntimeValidSystems(
  incoming: ConfigurationType[],
  existing: ConfigurationType[]
): ConfigurationType[] {
  const existingById = new Map(existing.map((c) => [c.uniqueId, c]));
  const inputContext = validSystemsInputContextFromState(store.getState());
  return incoming.map((config) => {
    const prev = existingById.get(config.uniqueId);
    if (!prev) return config;
    const prevCondById = new Map(prev.condensers.map((c) => [c.uniqueId, c]));
    return {
      ...config,
      condensers: config.condensers.map((cond) => {
        const prevCond = prevCondById.get(cond.uniqueId);
        if (!prevCond) return cond;
        const withPool =
          prevCond.airHandlerOptions &&
          getAirHandlerPoolState(prevCond.airHandlerOptions) !== null
            ? {
                ...cond,
                airHandlerOptions: withAirHandlerPoolStaleness(
                  prevCond.airHandlerOptions,
                  currentPoolHashOf(cond, inputContext)
                ),
                ductingSummary: prevCond.ductingSummary ?? null
              }
            : cond;
        if (!prevCond.validSystemsFetched) return withPool;
        return {
          ...withPool,
          validSystems: prevCond.validSystems,
          validDuctedSystems: prevCond.validDuctedSystems,
          validSystemsFetched: prevCond.validSystemsFetched,
          airHandlerOptions:
            withPool.airHandlerOptions ?? prevCond.airHandlerOptions,
          productCombinations: prevCond.productCombinations
        };
      })
    };
  });
}

export function isConfigStepAccessible(
  sections: SidebarSectionType[],
  stepId: string
): boolean {
  const eligible = sections.filter((s) => s.requirementsMet !== undefined);
  const targetIndex = eligible.findIndex((s) => s.id === stepId);
  if (targetIndex < 0) return true;
  const allPreviousComplete = eligible
    .slice(0, targetIndex)
    .every((s) => s.requirementsMet === true);
  return allPreviousComplete && eligible[targetIndex].disabled !== true;
}

export function filterConditionedSpaces(spaces: SpaceType[]): SpaceType[] {
  return spaces.filter((space) => space.conditioned !== false);
}

export function getUnconditionedSpaces(structure: any[]): SpaceType[] {
  const unconditionedSpaces: SpaceType[] = [];
  structure.forEach((level) => {
    level.sections.forEach((section: any) => {
      section.spaces.forEach((space: SpaceType) => {
        if (space.conditioned === false) {
          unconditionedSpaces.push(space);
        }
      });
    });
  });
  return unconditionedSpaces;
}

export function getZoneableSpaces(
  structure: any[],
  configurations: ConfigurationType[]
): SpaceType[] {
  const assignedSpaceIds = new Set<string>();

  configurations.forEach((config) => {
    config.condensers.forEach((condenser) => {
      condenser.zones.forEach((zone) => {
        zone.zoneItems.forEach((zoneItem) => {
          assignedSpaceIds.add(zoneItem.structureItemId);
        });
      });
    });
  });

  const zoneableSpaces: SpaceType[] = [];
  structure.forEach((level) => {
    level.sections.forEach((section: any) => {
      section.spaces.forEach((space: SpaceType) => {
        if (
          space.conditioned !== false &&
          !assignedSpaceIds.has(space.uniqueId)
        ) {
          zoneableSpaces.push(space);
        }
      });
    });
  });

  return zoneableSpaces;
}

export function truncateConfigurations(configs: ConfigurationType[]) {
  return configs.map((config) => ({
    ...config,
    condensers: config.condensers.map((cond) => ({
      ...cond,
      validSystems: [],
      validDuctedSystems: [],
      validSystemsFetched: false,
      airHandlerOptions: null
    }))
  }));
}

export function createConfiguration(
  baseData?: Partial<ConfigurationType>
): ConfigurationType {
  return {
    uniqueId: nanoid(5),
    name: 'New Configuration',
    type: 'ductless',
    condensers: [],
    ...baseData
  };
}

export function hasNonPlaceholderConfiguration(
  configurations: ConfigurationType[]
): boolean {
  return configurations.some(
    (configuration) =>
      configuration.aiGenerated || configuration.condensers.length > 0
  );
}

export function hasAiGeneratedConfiguration(
  configurations: ConfigurationType[]
): boolean {
  return configurations.some((configuration) => configuration.aiGenerated);
}

const WALL_MOUNT_OFFSET_FT = 4;
const DEFAULT_CEILING_HEIGHT_FT = 8;

export function calculateHeightOffGround(
  placementType: 'ground' | 'wall' | 'roof' | undefined,
  levelUniqueId: string | undefined,
  structure: LevelType[]
): number {
  if (!levelUniqueId || structure.length === 0) return 0;

  const groundLevelIndex = structure.findIndex(
    (level) => level.type !== 'basement' && level.type !== 'crawlspace'
  );
  const condenserLevelIndex = structure.findIndex(
    (l) => l.uniqueId === levelUniqueId
  );
  if (condenserLevelIndex === -1) return 0;

  let heightBelow = 0;
  for (let i = Math.max(groundLevelIndex, 0); i < condenserLevelIndex; i += 1) {
    const ceilingAttr = structure[i].attributes.find(
      (a) => a.code === 'ceiling_height'
    );
    heightBelow +=
      parseFloat(ceilingAttr?.value as string) || DEFAULT_CEILING_HEIGHT_FT;
  }

  const isElevated = placementType === 'wall' || placementType === 'roof';
  return heightBelow + (isElevated ? WALL_MOUNT_OFFSET_FT : 0);
}

export function createCondenser(
  baseData?: Partial<CondenserType>
): CondenserType {
  return {
    uniqueId: nanoid(5),
    type: 'ductless',
    name: 'New Condenser',
    zones: [],
    heightOffGround: 0,
    validSystems: [],
    validDuctedSystems: [],
    ductedAccessories: [],
    selectedAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    ...baseData
  };
}

export function createZone(baseData?: Partial<ZoneType>): ZoneType {
  return {
    uniqueId: nanoid(5),
    name: 'New Zone',
    type: 'ductless',
    zoneItems: [],
    ...baseData
  };
}

export function createZoneItem(baseData?: Partial<ZoneItemType>): ZoneItemType {
  return {
    uniqueId: nanoid(5),
    structureItemId: '',
    name: 'New Zone Item',
    BTUs: {
      heating: 0,
      cooling: 0
    },
    originalBTUs: {
      heating: 0,
      cooling: 0
    },
    ...baseData
  };
}

export function findConfigurationById(
  configurations: ConfigurationType[],
  configId: string
): ConfigurationType | undefined {
  return configurations.find((c) => c.uniqueId === configId);
}

export function findEmptyZone(zones: ZoneType[]): ZoneType | undefined {
  return zones.find((zone) => zone.zoneItems.length === 0);
}

export function findCondenserById(
  configuration: ConfigurationType,
  condenserId: string
): CondenserType | undefined {
  return configuration.condensers.find((cond) => cond.uniqueId === condenserId);
}

export function findZoneById(
  condenser: CondenserType,
  zoneId: string
): ZoneType | undefined {
  return condenser.zones.find((z) => z.uniqueId === zoneId);
}

export function createEmptyConfig(): ConfigurationType {
  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    optionId: 1,
    name: 'Custom Configuration',
    type: 'ductless',
    condensers: [
      {
        uniqueId: nanoid(5),
        type: 'ductless',
        name: 'Condenser 1',
        validSystems: [],
        validDuctedSystems: [],
        modified: false,
        ductedAccessories: [],
        selectedAccessories: [],
        accessoriesCompleted: [],
        accessoriesNotNeeded: [],
        zones: [
          {
            uniqueId: nanoid(5),
            name: 'Zone 1',
            type: 'ductless',
            modified: false,
            zoneItems: []
          }
        ]
      }
    ],
    valid: false,
    modified: false
  };

  return newConfig;
}

export function createDuctedConfig(ptac = false): ConfigurationType {
  const { structure } = store.getState().structure;
  const { configurationOptions } = store.getState().configurations;
  const existingLabels = configurationOptions.map((c) => c.name);
  const newCondensers: CondenserType[] = [];
  let newName = ptac ? 'PTAC Configuration' : 'Ducted Configuration';

  if (existingLabels.includes(newName)) {
    let configCount = 2;
    while (existingLabels.includes(`${newName} ${configCount}`)) {
      configCount += 1;
    }
    newName = `${newName} ${configCount}`;
  }

  structure
    .filter((level) => level.sections.length > 0)
    .forEach((level, index) => {
      const newZones: ZoneType[] = [
        {
          uniqueId: nanoid(5),
          name: ptac ? `PTAC Zone ${index + 1}` : `Ducted Zone ${index + 1}`,
          type: 'ducted',
          modified: false,
          zoneItems: level.sections.flatMap((section) =>
            filterConditionedSpaces(section.spaces)
              .filter((s) => s.BTUs.heating || s.BTUs.cooling)
              .map((space) => ({
                uniqueId: nanoid(5),
                structureItemId: space.uniqueId,
                name: space.label,
                BTUs: space.BTUs,
                originalBTUs: space.BTUs,
                isPartition: false
              }))
          )
        }
      ];

      newCondensers.push({
        uniqueId: nanoid(5),
        type: 'ducted',
        name: ptac ? `PTAC ${index + 1}` : `Condenser ${index + 1}`,
        validSystems: [],
        validDuctedSystems: [],
        modified: false,
        ductedAccessories: [],
        selectedAccessories: [],
        accessoriesCompleted: [],
        accessoriesNotNeeded: [],
        zones: newZones
      });
    });

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: newName,
    type: 'ducted',
    condensers: newCondensers,
    valid: false,
    modified: false
  };

  return newConfig;
}

export function createDuctedConfigWithReplacementSystems(
  ptac = false
): ConfigurationType {
  const { structure, existingSystems } = store.getState().structure;
  const condensers: CondenserType[] = [];

  existingSystems.forEach((system, index) => {
    const newZones: ZoneType[] = [
      {
        uniqueId: nanoid(5),
        name: ptac ? 'PTAC Zone' : 'Ducted Zone',
        type: 'ducted',
        modified: false,
        zoneItems: structure.flatMap((level) =>
          level.sections.flatMap((section) =>
            filterConditionedSpaces(section.spaces)
              .filter((s) => s.BTUs.heating || s.BTUs.cooling)
              .map((space) => ({
                uniqueId: nanoid(5),
                structureItemId: space.uniqueId,
                name: space.label,
                BTUs: space.BTUs,
                originalBTUs: space.BTUs,
                isPartition: false
              }))
          )
        ),
        existingSystemId: system.uniqueId
      }
    ];

    condensers.push({
      uniqueId: nanoid(5),
      type: 'ducted',
      name: ptac ? `PTAC ${index + 1}` : `Condenser ${index + 1}`,
      validSystems: [],
      validDuctedSystems: [],
      modified: false,
      ductedAccessories: [],
      selectedAccessories: [],
      accessoriesCompleted: [],
      accessoriesNotNeeded: [],
      zones: newZones
    });
  });

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: ptac ? 'PTAC Configuration' : 'Ducted Configuration',
    type: 'ducted',
    condensers,
    valid: false,
    modified: false
  };

  return newConfig;
}

export function createHybridConfig(): ConfigurationType {
  const { structure, supplementalMiniSplitSpaces } = store.getState().structure;
  const { configurationOptions } = store.getState().configurations;
  const existingLabels = configurationOptions.map((c) => c.name);
  const newCondensers: CondenserType[] = [];
  let newName = 'Hybrid Configuration';

  if (existingLabels.includes(newName)) {
    let configCount = 2;
    while (existingLabels.includes(`${newName} ${configCount}`)) {
      configCount += 1;
    }
    newName = `${newName} ${configCount}`;
  }

  const supplementalZones: ZoneType[] = supplementalMiniSplitSpaces
    .map((spaceId, suppIndex) => {
      const space = findSpaceById(structure, spaceId);

      return space
        ? {
            uniqueId: nanoid(5),
            name: `Ductless Zone ${suppIndex + 1}`,
            type: 'ductless',
            modified: false,
            zoneItems: [
              {
                uniqueId: nanoid(5),
                name: space.label,
                BTUs: space.BTUs,
                originalBTUs: space.BTUs,
                isPartition: false,
                structureItemId: space.uniqueId
              }
            ]
          }
        : null;
    })
    .filter((zone) => zone !== null) as ZoneType[];

  structure
    .filter((level) => level.sections.length > 0)
    .forEach((level, index) => {
      const newZones: ZoneType[] = [
        {
          uniqueId: nanoid(5),
          name: `Ducted Zone ${index + 1}`,
          type: 'ducted',
          modified: false,
          zoneItems: level.sections.flatMap((section) =>
            filterConditionedSpaces(section.spaces)
              .filter((s) => s.BTUs.heating || s.BTUs.cooling)
              .map((space) => ({
                uniqueId: nanoid(5),
                structureItemId: space.uniqueId,
                name: space.label,
                BTUs: space.BTUs,
                originalBTUs: space.BTUs,
                isPartition: false
              }))
          )
        },
        ...supplementalZones.filter((zone) =>
          zone.zoneItems.some((zoneItem) =>
            level.sections.some((section) =>
              section.spaces.some(
                (space) => space.uniqueId === zoneItem.structureItemId
              )
            )
          )
        )
      ];

      newCondensers.push({
        uniqueId: nanoid(5),
        type: 'hybrid',
        name: `Condenser ${index + 1}`,
        validSystems: [],
        validDuctedSystems: [],
        modified: false,
        ductedAccessories: [],
        selectedAccessories: [],
        accessoriesCompleted: [],
        accessoriesNotNeeded: [],
        zones: newZones
      });
    });

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: newName,
    type: 'hybrid',
    condensers: newCondensers,
    valid: false,
    modified: false
  };

  return newConfig;
}

export function createHybridConfigWithReplacementSystems(): ConfigurationType {
  const { structure, supplementalMiniSplitSpaces, existingSystems } =
    store.getState().structure;
  const condensers: CondenserType[] = [];

  existingSystems.forEach((system, index) => {
    const newZones: ZoneType[] = [
      {
        uniqueId: nanoid(5),
        name: `Ducted Zone ${index + 1}`,
        type: 'ducted',
        modified: false,
        zoneItems: structure.flatMap((level) =>
          level.sections.flatMap((section) =>
            filterConditionedSpaces(section.spaces)
              .filter((s) => s.BTUs.heating || s.BTUs.cooling)
              .map((space) => ({
                uniqueId: nanoid(5),
                structureItemId: space.uniqueId,
                name: space.label,
                BTUs: space.BTUs,
                originalBTUs: space.BTUs,
                isPartition: false
              }))
          )
        ),
        existingSystemId: system.uniqueId
      },
      ...(supplementalMiniSplitSpaces
        .map((spaceId, suppIndex) => {
          const space = findSpaceById(structure, spaceId);

          return space
            ? {
                uniqueId: nanoid(5),
                name: `Ductless Zone ${suppIndex + 1}`,
                type: 'ductless',
                modified: false,
                zoneItems: [
                  {
                    uniqueId: nanoid(5),
                    name: space.label,
                    BTUs: space.BTUs,
                    originalBTUs: space.BTUs,
                    isPartition: false,
                    structureItemId: space.uniqueId
                  }
                ]
              }
            : null;
        })
        .filter((zone) => zone !== null) as ZoneType[])
    ];

    condensers.push({
      uniqueId: nanoid(5),
      type: 'hybrid',
      name: `Condenser ${index + 1}`,
      heightOffGround: 0,
      validSystems: [],
      validDuctedSystems: [],
      modified: false,
      ductedAccessories: [],
      selectedAccessories: [],
      accessoriesCompleted: [],
      accessoriesNotNeeded: [],
      zones: newZones
    });
  });

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: 'Hybrid Configuration',
    type: 'hybrid',
    condensers,
    valid: false,
    modified: false
  };

  return newConfig;
}

export function createLayoutBasedConfigs(): ConfigurationType[] {
  const { structure } = store.getState().structure;

  const newCondensers: CondenserType[] = structure
    .filter((level) => level.sections.length > 0)
    .map((level, levelIndex) => ({
      uniqueId: nanoid(5),
      type: 'ductless',
      name: `Condenser ${levelIndex + 1}`,
      heightOffGround: 0,
      validSystems: [],
      validDuctedSystems: [],
      ductedAccessories: [],
      selectedAccessories: [],
      accessoriesCompleted: [],
      accessoriesNotNeeded: [],
      zones: level.sections.map((section, idx) => ({
        uniqueId: nanoid(5),
        name: `Zone ${idx + 1}`,
        type: 'ductless',
        zoneItems: filterConditionedSpaces(section.spaces).map((space) => ({
          uniqueId: nanoid(5),
          structureItemId: space.uniqueId,
          name: space.label,
          BTUs: space.BTUs,
          originalBTUs: space.BTUs
        }))
      }))
    }));

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: 'Layout Based Configuration',
    type: 'ductless',
    condensers: newCondensers
  };

  return [newConfig];
}

export function createIndividualZonesConfigs(): ConfigurationType[] {
  const { structure } = store.getState().structure;
  let zoneCount = 0;

  const newCondensers: CondenserType[] = structure
    .filter((level) => level.sections.length > 0)
    .map((level, levelIndex) => ({
      uniqueId: nanoid(5),
      type: 'ductless',
      name: `Condenser ${levelIndex + 1}`,
      heightOffGround: 0,
      validSystems: [],
      validDuctedSystems: [],
      ductedAccessories: [],
      selectedAccessories: [],
      accessoriesCompleted: [],
      accessoriesNotNeeded: [],
      zones: level.sections
        .flatMap((section) =>
          filterConditionedSpaces(section.spaces).filter(
            (s) => s.BTUs.heating || s.BTUs.cooling
          )
        )
        .map((space) => {
          zoneCount += 1;
          return {
            uniqueId: nanoid(5),
            name: `Zone ${zoneCount}`,
            type: 'ductless',
            zoneItems: [
              {
                uniqueId: nanoid(5),
                structureItemId: space.uniqueId,
                name: space.label,
                BTUs: space.BTUs,
                originalBTUs: space.BTUs
              }
            ]
          };
        })
    }));

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: 'Zone-Per-Space Configuration',
    type: 'ductless',
    condensers: newCondensers
  };

  return [newConfig];
}

export function createSingleCondenserConfigs(): ConfigurationType[] {
  const { structure } = store.getState().structure;

  let zoneCount = 0;

  const newCondenser: CondenserType = {
    uniqueId: nanoid(5),
    type: 'ductless',
    name: 'Condenser 1',
    heightOffGround: 0,
    validSystems: [],
    validDuctedSystems: [],
    ductedAccessories: [],
    selectedAccessories: [],
    accessoriesCompleted: [],
    accessoriesNotNeeded: [],
    zones: structure.flatMap((level) =>
      level.sections.map((section) => {
        zoneCount += 1;

        return {
          uniqueId: nanoid(5),
          name: `Zone ${zoneCount}`,
          type: 'ductless',
          zoneItems: filterConditionedSpaces(section.spaces)
            .filter((s) => s.BTUs.heating || s.BTUs.cooling)
            .map((space) => ({
              uniqueId: nanoid(5),
              structureItemId: space.uniqueId,
              name: space.label,
              BTUs: space.BTUs,
              originalBTUs: space.BTUs
            }))
        };
      })
    )
  };

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: 'Single Condenser Configuration',
    type: 'ductless',
    condensers: [newCondenser]
  };

  return [newConfig];
}

export function createOneCondenserPerZoneConfigs(): ConfigurationType[] {
  const { structure } = store.getState().structure;

  let condenserCount = 0;

  const newConfig: ConfigurationType = {
    uniqueId: nanoid(5),
    name: 'One Condenser Per Zone Configuration',
    type: 'ductless',
    condensers: structure
      .flatMap((level) => level.sections)
      .map((section) => {
        condenserCount += 1;

        return {
          uniqueId: nanoid(5),
          name: `Condenser ${condenserCount}`,
          type: 'ductless',
          heightOffGround: 0,
          validSystems: [],
          validDuctedSystems: [],
          ductedAccessories: [],
          selectedAccessories: [],
          accessoriesCompleted: [],
          accessoriesNotNeeded: [],
          zones: [
            {
              uniqueId: nanoid(5),
              name: 'Zone 1',
              type: 'ductless',
              modified: false,
              zoneItems: filterConditionedSpaces(section.spaces)
                .filter((s) => s.BTUs.heating || s.BTUs.cooling)
                .map((space) => ({
                  uniqueId: nanoid(5),
                  structureItemId: space.uniqueId,
                  name: space.label,
                  BTUs: space.BTUs,
                  originalBTUs: space.BTUs
                }))
            }
          ]
        };
      })
  };

  return [newConfig];
}

function createZoneItemFromSpace(space: SpaceType): ZoneItemType {
  return {
    uniqueId: nanoid(5),
    structureItemId: space.uniqueId,
    name: space.label,
    BTUs: space.BTUs,
    originalBTUs: space.BTUs
  };
}

function createZoneFromSpaces(
  spaces: SpaceType[],
  zoneNumber: number
): ZoneType {
  return {
    uniqueId: nanoid(5),
    name: `Zone ${zoneNumber}`,
    type: 'ductless',
    zoneItems: spaces.map(createZoneItemFromSpace)
  };
}

function createSplitZonesForSection(
  spacesThatCanBeAlone: SpaceType[],
  allSectionSpaces: SpaceType[],
  startingZoneCount: number
): { zones: ZoneType[]; newZoneCount: number } {
  let zoneCount = startingZoneCount;
  const zones: ZoneType[] = [];

  const keptSpace = spacesThatCanBeAlone[0];
  const splitOutSpaces = spacesThatCanBeAlone.filter(
    (s) => s.uniqueId !== keptSpace.uniqueId
  );

  splitOutSpaces.forEach((space) => {
    zoneCount += 1;
    zones.push({
      uniqueId: nanoid(5),
      name: `Zone ${zoneCount}`,
      type: 'ductless',
      zoneItems: [createZoneItemFromSpace(space)]
    });
  });

  const splitOutIds = new Set(splitOutSpaces.map((s) => s.uniqueId));
  const remainingSpaces = filterConditionedSpaces(allSectionSpaces).filter(
    (s) => !splitOutIds.has(s.uniqueId)
  );

  if (remainingSpaces.length > 0) {
    zoneCount += 1;
    zones.push(createZoneFromSpaces(remainingSpaces, zoneCount));
  }

  return { zones, newZoneCount: zoneCount };
}

function createDefaultZoneForSection(
  sectionSpaces: SpaceType[],
  zoneNumber: number
): ZoneType {
  return createZoneFromSpaces(
    filterConditionedSpaces(sectionSpaces).filter(
      (s) => s.BTUs.heating || s.BTUs.cooling
    ),
    zoneNumber
  );
}

export function createSplitZoneConfigurations(): ConfigurationType[] {
  const { structure } = store.getState().structure;
  const newConfigs: ConfigurationType[] = [];

  structure.forEach((level) => {
    level.sections.forEach((section) => {
      if (section.spaces.length <= 1) return;

      const spacesThatCanBeAlone = filterConditionedSpaces(
        section.spaces
      ).filter((space) => getItem(space.itemId)?.canBeAlone);

      if (spacesThatCanBeAlone.length <= 1) return;

      let zoneCount = 0;
      const condensers = structure
        .filter((lvl) => lvl.sections.length > 0)
        .map((lvl, levelIndex) => {
          const zones = lvl.sections.flatMap((sec) => {
            if (sec.uniqueId === section.uniqueId) {
              const result = createSplitZonesForSection(
                spacesThatCanBeAlone,
                sec.spaces,
                zoneCount
              );
              zoneCount = result.newZoneCount;
              return result.zones;
            }
            zoneCount += 1;
            return [createDefaultZoneForSection(sec.spaces, zoneCount)];
          });

          return {
            uniqueId: nanoid(5),
            name: `Condenser ${levelIndex + 1}`,
            type: 'ductless' as const,
            heightOffGround: 0,
            validSystems: [],
            validDuctedSystems: [],
            ductedAccessories: [],
            selectedAccessories: [],
            accessoriesCompleted: [],
            accessoriesNotNeeded: [],
            zones
          };
        });

      newConfigs.push({
        uniqueId: nanoid(5),
        name: 'Split Zone Configuration',
        type: 'ductless',
        condensers
      });
    });
  });

  return newConfigs;
}

export function updateConfigZoneItems(configs: ConfigurationType[]) {
  const { structure } = store.getState().structure;

  return produce(configs, (draft) => {
    draft.forEach((config) => {
      config.valid = false;
      config.condensers.forEach((cond) => {
        cond.validSystems = [];
        cond.validDuctedSystems = [];
        cond.validSystemsFetched = false;
        cond.airHandlerOptions = null;
        cond.selectedProduct = undefined;
        cond.zones.forEach((zone) => {
          zone.selectedProduct = undefined;
          zone.zoneItems.forEach((zoneItem) => {
            const structureItem = findSpaceById(
              structure,
              zoneItem.structureItemId
            );

            if (!structureItem) {
              return;
            }

            zoneItem.name = structureItem.label;
            zoneItem.originalBTUs = structureItem.BTUs;

            if (zoneItem.isPartition) {
              const partitionCount = zone.zoneItems.filter(
                (item) => item.structureItemId === structureItem.uniqueId
              ).length;
              zoneItem.BTUs = {
                ...structureItem.BTUs,
                heating: structureItem.BTUs.heating / partitionCount,
                cooling: structureItem.BTUs.cooling / partitionCount
              };
            } else {
              zoneItem.BTUs = structureItem.BTUs;
            }
          });
        });
      });
    });
  });
}

export function markCondensersStale(
  config: ConfigurationType,
  condenserIds?: string[]
): ConfigurationType {
  const idSet = condenserIds ? new Set(condenserIds) : null;
  const isTargeted = (condenser: CondenserType): boolean =>
    !idSet || idSet.has(condenser.uniqueId);
  const inputContext = validSystemsInputContextFromState(store.getState());
  const currentPoolHashById = new Map(
    config.condensers
      .filter(
        (condenser) =>
          isTargeted(condenser) &&
          getAirHandlerPoolState(condenser.airHandlerOptions) !== null
      )
      .map(
        (condenser) =>
          [
            condenser.uniqueId,
            currentPoolHashOf(condenser, inputContext)
          ] as const
      )
  );
  return produce(config, (draft) => {
    draft.condensers.forEach((condenser) => {
      if (!isTargeted(condenser)) return;
      condenser.validSystemsFetched = false;
      if (!condenser.airHandlerOptions) return;
      if (!currentPoolHashById.has(condenser.uniqueId)) return;
      condenser.airHandlerOptions = withAirHandlerPoolStaleness(
        condenser.airHandlerOptions,
        currentPoolHashById.get(condenser.uniqueId) ?? null
      );
    });
  });
}

export const resetValidSystemsFetched = markCondensersStale;

export function findZoneContainingSpace(
  config: ConfigurationType,
  spaceId: string
): { condenser: CondenserType; zone: ZoneType } | null {
  for (const condenser of config.condensers) {
    for (const zone of condenser.zones) {
      if (zone.zoneItems.some((zi) => zi.structureItemId === spaceId)) {
        return { condenser, zone };
      }
    }
  }
  return null;
}

export function countMatchingValidSystems(
  condenser: CondenserType | undefined
): number {
  return (condenser?.validSystems ?? []).filter(
    (system) => system.isMatchingAirHandlers === true
  ).length;
}

export function findZoneNameByUniqueId(
  config: ConfigurationType | undefined,
  zoneUniqueId: string
): string | null {
  if (!config) return null;
  for (const condenser of config.condensers) {
    for (const zone of condenser.zones) {
      if (zone.uniqueId === zoneUniqueId) {
        return zone.name || null;
      }
    }
  }
  return null;
}

export function findZoneUniqueIdByZoneItemUniqueId(
  config: ConfigurationType | undefined,
  zoneItemUniqueId: string
): string | null {
  if (!config) return null;
  for (const condenser of config.condensers) {
    for (const zone of condenser.zones) {
      if (zone.zoneItems.some((zi) => zi.uniqueId === zoneItemUniqueId)) {
        return zone.uniqueId;
      }
    }
  }
  return null;
}

export function findCondenserUniqueIdByZoneUniqueId(
  config: ConfigurationType | undefined,
  zoneUniqueId: string
): string | null {
  if (!config) return null;
  for (const condenser of config.condensers) {
    if (condenser.zones.some((zone) => zone.uniqueId === zoneUniqueId)) {
      return condenser.uniqueId;
    }
  }
  return null;
}

export function findZoneByAirHandlerId(
  config: ConfigurationType,
  airHandlerUniqueId: string
): {
  condenser: CondenserType;
  zone: ZoneType;
  condenserIndex: number;
  zoneIndex: number;
} | null {
  for (
    let condenserIndex = 0;
    condenserIndex < config.condensers.length;
    condenserIndex += 1
  ) {
    const condenser = config.condensers[condenserIndex];
    for (
      let zoneIndex = 0;
      zoneIndex < condenser.zones.length;
      zoneIndex += 1
    ) {
      const zone = condenser.zones[zoneIndex];
      if (
        zone.airHandler?.uniqueId === airHandlerUniqueId ||
        zone.zoneItems.some((zi) => zi.uniqueId === airHandlerUniqueId)
      ) {
        return { condenser, zone, condenserIndex, zoneIndex };
      }
    }
  }
  return null;
}

export function getLevelForZone(
  zone: ZoneType,
  structure: LevelType[]
): LevelType | null {
  const firstZoneItem = zone.zoneItems[0];
  if (!firstZoneItem) return null;

  for (const level of structure) {
    for (const section of level.sections) {
      if (
        section.spaces.some((s) => s.uniqueId === firstZoneItem.structureItemId)
      ) {
        return level;
      }
    }
  }
  return null;
}

export function getLevelIndexForZone(
  zone: ZoneType,
  structure: LevelType[]
): number {
  const spaceIds = new Set(zone.zoneItems.map((item) => item.structureItemId));
  if (spaceIds.size === 0) return -1;

  return structure.findIndex((level) =>
    level.sections.some((section) =>
      section.spaces.some((space) => spaceIds.has(space.uniqueId))
    )
  );
}

export function renumberZones(config: ConfigurationType): ConfigurationType {
  return produce(config, (draft) => {
    let zoneNumber = 1;
    draft.condensers.forEach((condenser) => {
      condenser.zones.forEach((zone) => {
        zone.name = `Zone ${zoneNumber}`;
        zoneNumber += 1;
      });
    });
  });
}

export function getUnzonedSpaces(
  structure: LevelType[],
  configuration: ConfigurationType | undefined
): SpaceType[] {
  const allZonedSpaceIds = new Set<string>();

  configuration?.condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      zone.zoneItems.forEach((item) => {
        allZonedSpaceIds.add(item.structureItemId);
      });
    });
  });

  const unzonedSpaces: SpaceType[] = [];

  structure.forEach((level) => {
    level.sections.forEach((section) => {
      section.spaces.forEach((space) => {
        if (
          space.conditioned !== false &&
          !allZonedSpaceIds.has(space.uniqueId)
        ) {
          unzonedSpaces.push(space);
        }
      });
    });
  });

  return unzonedSpaces;
}

export function getSectionSpaces(
  structure: LevelType[],
  sectionId: string
): SpaceType[] {
  for (const level of structure) {
    for (const section of level.sections) {
      if (section.uniqueId === sectionId) {
        return section.spaces.filter((space) => space.conditioned !== false);
      }
    }
  }
  return [];
}

export function findSectionIdForSpace(
  structure: LevelType[],
  spaceId: string
): string | null {
  for (const level of structure) {
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === spaceId) {
          return section.uniqueId;
        }
      }
    }
  }
  return null;
}

export function findLevelIndexForSpace(
  structure: LevelType[],
  spaceId: string
): number | null {
  for (let levelIndex = 0; levelIndex < structure.length; levelIndex += 1) {
    const level = structure[levelIndex];
    for (const section of level.sections) {
      for (const space of section.spaces) {
        if (space.uniqueId === spaceId) {
          return levelIndex;
        }
      }
    }
  }
  return null;
}

export function isZonesStepCompleteForLevel(
  level: LevelType,
  configuration: ConfigurationType | undefined
): boolean {
  if (!configuration) {
    return false;
  }

  const conditionedSpaceIds = new Set<string>();
  level.sections.forEach((section) => {
    section.spaces.forEach((space) => {
      if (space.conditioned !== false) {
        conditionedSpaceIds.add(space.uniqueId);
      }
    });
  });

  if (conditionedSpaceIds.size === 0) {
    return true;
  }

  const zonedSpaceIds = new Set<string>();
  configuration.condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      zone.zoneItems.forEach((item) => {
        if (conditionedSpaceIds.has(item.structureItemId)) {
          zonedSpaceIds.add(item.structureItemId);
        }
      });
    });
  });

  return conditionedSpaceIds.size === zonedSpaceIds.size;
}

export function isAirHandlersStepCompleteForLevel(
  level: LevelType,
  configuration: ConfigurationType | undefined
): boolean {
  if (!configuration) {
    return false;
  }

  const levelSpaceIds = new Set<string>();
  level.sections.forEach((section) => {
    section.spaces.forEach((space) => {
      if (space.conditioned !== false) {
        levelSpaceIds.add(space.uniqueId);
      }
    });
  });

  if (levelSpaceIds.size === 0) {
    return true;
  }

  const zonesWithLevelSpaces: { hasAirHandler: boolean }[] = [];

  configuration.condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      const hasSpaceFromLevel = zone.zoneItems.some((item) =>
        levelSpaceIds.has(item.structureItemId)
      );

      if (hasSpaceFromLevel && zone.zoneItems.length > 0) {
        zonesWithLevelSpaces.push({
          hasAirHandler: zone.airHandler !== undefined
        });
      }
    });
  });

  if (zonesWithLevelSpaces.length === 0) {
    return false;
  }

  return zonesWithLevelSpaces.every((z) => z.hasAirHandler);
}

export function isLinesetsStepCompleteForLevel(
  level: LevelType,
  configuration: ConfigurationType | undefined
): boolean {
  if (!configuration) {
    return false;
  }

  const levelSpaceIds = new Set<string>();
  level.sections.forEach((section) => {
    section.spaces.forEach((space) => {
      if (space.conditioned !== false) {
        levelSpaceIds.add(space.uniqueId);
      }
    });
  });

  if (levelSpaceIds.size === 0) {
    return true;
  }

  const airHandlerUniqueIds: string[] = [];

  configuration.condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      const hasSpaceFromLevel = zone.zoneItems.some((item) =>
        levelSpaceIds.has(item.structureItemId)
      );

      if (hasSpaceFromLevel && zone.airHandler?.uniqueId) {
        airHandlerUniqueIds.push(zone.airHandler.uniqueId);
      }
    });
  });

  if (airHandlerUniqueIds.length === 0) {
    return false;
  }

  return airHandlerUniqueIds.every((airHandlerId) => {
    return configuration.lineSets?.some(
      (ls) => ls.airHandlerUniqueId === airHandlerId
    );
  });
}
